// The Label per-artist dashboard (`/roster/artists/:artistId`) — the landing when an artist
// is opened under the org lens. The shared top row — Needs attention · Opportunities · To-do (ART-324) — over
// Music / Marketing / Content / Trending, with "Open tab →" affordances. MONEY-FREE by construction: the DTO
// carries no money field; each card's numbers are summaries of the SAME projections the deep tabs read.
// Reads the band from the mounted RosterBandProvider (useBandContext) — the org-lens middleware auto-resolves the
// money-free `/orgs/{orgId}/bands/{bandId}/dashboard` mirror. Four-state contract; born-blank fields render '—'.
import { useRosterArtistDashboard } from "../../app/hooks/useRosterArtistDashboard.js";
import { useRosterHeaderAction } from "../../app/rosterHeaderAction.js";
import { useRosterHeaderPills } from "../../app/rosterHeaderPills.js";
import { useArtistReleaseCard } from "../../app/hooks/useReleaseCards.js";
import type { ReleaseSectionKey } from "../releases/sections.js";
import { ArtistReleaseCard, LastReleaseStrip } from "./ReleaseCards.js";
import { RosterArtistScreenShell, type RosterScreenState } from "./RosterScreenShell.js";
import {
  ArtistAttentionRow, MusicCard, MarketingCard, TrendingCard,
} from "./rosterArtistCards.js";
import styles from "./RosterArtist.module.scss";

export function RosterArtistDashboard({
  onOpenTab,
  onOpenTask,
  onOpenRelease,
}: {
  onOpenTab: (tabKey: string) => void;
  onOpenTask?: (taskId: string) => void;
  /** The release card (ART-335): open a release, at one of its sections. */
  onOpenRelease?: (releaseId: string, section?: ReleaseSectionKey) => void;
}) {
  const q = useRosterArtistDashboard();
  const releaseCard = useArtistReleaseCard();   // reads the band from the mounted RosterBandProvider (useBandQuery)
  const data = q.data;

  // The shell's context header is the one header, so this page's action registers into it rather than
  // rendering a second one (PO, 2026-09-02). Refresh REVALIDATES in the background — the composed read
  // stays on screen while it refetches, so the page never flashes back through its own skeleton.
  useRosterHeaderAction("Refresh", () => void q.refetch());
  // The artist's social following in the shell header (ART-334) — it replaces the Content card below.
  useRosterHeaderPills(data?.content?.platforms);

  // The shared per-artist frame owns the loading skeleton and the error retry; this screen owns its
  // sections. Its default skeleton — a header block over a four-card grid — is exactly this page's shape.
  const state: RosterScreenState = q.isLoading
    ? { status: "loading" }
    : q.isError || !data
      ? { status: "error", error: q.error, onRetry: () => void q.refetch() }
      : { status: "ready" };

  return (
    <RosterArtistScreenShell state={state}>
      {data && (
        <>
      {/* NO page header (PO, 2026-09-02): the roster shell's context header is the ONE header, as it always
          was on the Booking lens. This page stacked a second one under it, naming the artist twice.
          Nothing is lost with it — the release chip it pinned right is the SAME `ReleaseChip` the Marketing
          card already renders as its headline and its Rollout row, and the audience figures moved into the
          shell's own meta line, which the rail was already computing. */}

          {/* The shared top row (ART-324): Needs attention · Opportunities · To-do, above Music, Marketing and
              Content. The To-do card moved INTO it, which hands its grid cell to Trending. A label reads the
              schedule and does not edit it, so its Needs attention rows offer no Open. */}
          {/* The release card (ART-335): the artist's soonest upcoming release, else their most recent. */}
          {releaseCard.data?.card && onOpenRelease && (
            <ArtistReleaseCard card={releaseCard.data.card} onOpenRelease={onOpenRelease} />
          )}

          <ArtistAttentionRow
            opportunities={data.opportunities}
            todos={data.todos}
            onOpenTask={onOpenTask}
            onChanged={() => void q.refetch()}
          />

          {/* ART-335: the last release already out, then Trending at full width — the label keeps an eye on a
              release once the next one takes the card, and a spike matters as much as the release. */}
          {releaseCard.data?.lastRelease && onOpenRelease && (
            <LastReleaseStrip release={releaseCard.data.lastRelease} onOpen={(id) => onOpenRelease(id)} />
          )}
          <TrendingCard trending={data.trending} />

          {/* Music beside Marketing (ART-334). The Content card went: its follower counts are the header's
              pills now. */}
          <div className={styles.cardGrid}>
            <MusicCard music={data.music} onOpen={() => onOpenTab("music")} />
            <MarketingCard marketing={data.marketing} onOpen={() => onOpenTab("marketing")} />
          </div>
        </>
      )}
    </RosterArtistScreenShell>
  );
}
